/**
 * Área "Financeiro" do painel, idêntica à aba Financeiro do admin atual (`admin.html:1858` a `1912` e
 * `8128` a `8741`; linhas 86 a 114 do inventário `docs/PARIDADE-ADMIN.md`).
 *
 * O QUE O LEGADO FAZ E ESTA TELA REPETE, e isto importa para entender o estado abaixo: o resumo, as
 * listas e os cartões dos planos são desenhados de uma vez (`renderizarFinanceiro`), e só são
 * redesenhados ao carregar, ao Filtrar, ao Limpar e ao quitar um lead. Marcar, desmarcar e editar
 * uma parcela trocam SÓ a linha da parcela e o "N/M parcelas pagas" do cartão; o resumo, os
 * subtotais do cartão e a lixeira ficam como estavam até o próximo redesenho. Por isso a tela guarda
 * o `desenho` (o que foi calculado no último redesenho) separado dos dados vivos.
 *
 * A ROTA: `/financeiro?de=AAAA-MM-DD&ate=AAAA-MM-DD` abre com as datas já filtradas (o clique do
 * Painel), e `/financeiro?registrar=<id do lead>` abre o modal "Registrar pagamento" (a Vendas abre
 * assim o que o legado abria por cima do Kanban).
 *
 * OS ACHADOS 3 E 6 do inventário (seção 4) estão reproduzidos, cada um isolado num ponto só.
 */

// Estado, efeito, memória e referência do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// As peças do Chassi.
import { Botao, Campo, CampoTexto, Cartao, IconeLixeira, Selecao, Selo, Tabela, type Coluna } from '@complexity/ui';
// As linhas do banco.
import type {
  LinhaCuponsDescontoResgates,
  LinhaGiftCards,
  LinhaLeadsFunil,
  LinhaParcelas,
} from '@studiolu/dados';
// As chamadas da área, cada uma com a linha do legado.
import {
  desmarcarParcela,
  editarParcela,
  excluirPagamentoDoLead,
  gravarQuitacaoComDiferenca,
  lerEtapaAnteriorAoPagamento,
  lerStatusDoLead,
  listarCuponsAplicados,
  listarLeadsDoFunil,
  listarPagamentosDosLeads,
  listarParcelasDosPagamentos,
  marcarParcelaComoPaga,
  mudarStatusDoLead,
} from '@studiolu/dados/areas/financeiro';
// Os gift cards são lidos pela mesma função da área Sistema, como no legado (`carregarGiftCardsDados`).
import { listarGiftCards } from '@studiolu/dados/areas/sistema';
// O contexto, a rota e os papéis.
import { usePainel } from '../../contexto.js';
import { filtroDaRota } from '../../rotas.js';
import { permissoesDoPapel } from '../../papeis.js';
// As peças comuns do admin.
import { formatarMoeda, formatarTelefone } from '../../comum/formatos.js';
import { mascararMoedaLegado, parseMoeda } from '../../comum/moeda.js';
import { useConfirmacao } from '../../comum/confirmacao.js';
import { ModalLegado } from '../../comum/modal-legado.js';
import { GrupoRadio } from '../../comum/grupo-radio.js';
import { LinkWhatsapp } from '../../comum/whatsapp.js';
// As regras puras, o relatório e o modal de pagamento.
import {
  FILTROS_VAZIOS,
  FORMAS_PAGAMENTO,
  ROTULO_STATUS_PARCELA,
  filtrarCupons,
  filtrarGiftCards,
  filtrarPlanos,
  limitesDeData,
  montarHtmlRelatorio,
  nomeDoLead,
  seloDoGiftCard,
  somarParcelas,
  statusParcela,
  textoDoGiftCard,
  totaisDoResumo,
  type Agrupamento,
  type FiltrosFinanceiro,
  type PlanoFinanceiro,
  type StatusParcela,
  type TotaisDoResumo,
} from './regras.js';
import { abrirRelatorio, coresDoRelatorio } from './relatorio.js';
import { RegistrarPagamento } from './RegistrarPagamento.js';
import { dataBR, dataHoraBR, hojeUTC, LinhaCompacta } from './apoio.js';

/**
 * ACHADO 3, reproduzido por decisão do fundador: o papel Financeiro VÊ a lixeira do plano, porque a
 * classe dela (`.fin-card-excluir`) não está no seletor que esconde exclusão de quem não é
 * administrador (`admin.html:8343` x `3375`). `false` deixa a lixeira só para o administrador.
 */
const ACHADO_3_FINANCEIRO_VE_LIXEIRA = true;

// O tom do selo de cada estado de parcela: as cores dos `.badge-*` do legado (`admin.html:1105` a `1107`).
const TOM_DO_SELO: Record<StatusParcela, 'sucesso' | 'erro' | 'neutro'> = { pago: 'sucesso', atrasada: 'erro', pendente: 'neutro' };

// As opções dos dois filtros de escolha, na ordem do legado (`admin.html:1871` e `1880`).
const OPCOES_STATUS = [
  { valor: 'atrasada', rotulo: 'Atrasada' },
  { valor: 'pago', rotulo: 'Pago' },
  { valor: 'pendente', rotulo: 'Pendente' },
] as const;
const OPCOES_FORMA_FILTRO = [
  { valor: 'boleto', rotulo: 'Boleto' },
  { valor: 'cartao_credito', rotulo: 'Cartão de crédito' },
  { valor: 'dinheiro', rotulo: 'Dinheiro' },
  { valor: 'pix', rotulo: 'PIX' },
  { valor: 'transferencia', rotulo: 'Transferência bancária' },
] as const;

// As três escolhas do Relatório PDF (`admin.html:3000` a `3008`).
const OPCOES_AGRUPAMENTO = [
  { valor: 'mes', rotulo: 'Por mês' },
  { valor: 'cliente', rotulo: 'Por cliente' },
  { valor: 'status', rotulo: 'Por status' },
] as const;

/** Tudo que a tela carregou, num objeto só, para os fluxos assíncronos lerem sempre o mais novo. */
interface Mundo {
  readonly leads: readonly LinhaLeadsFunil[];
  readonly giftCards: readonly LinhaGiftCards[];
  readonly cupons: readonly LinhaCuponsDescontoResgates[];
  readonly planos: readonly PlanoFinanceiro[];
}
const MUNDO_VAZIO: Mundo = { leads: [], giftCards: [], cupons: [], planos: [] };

/** O que o último redesenho calculou, e que só muda no próximo redesenho, como no legado. */
interface Desenho {
  readonly resumo: TotaisDoResumo;
  readonly giftCards: readonly LinhaGiftCards[];
  readonly cupons: readonly LinhaCuponsDescontoResgates[];
  // Os planos que passaram no filtro, na ordem da lista.
  readonly planos: readonly number[];
  // Recebido e a receber de cada cartão, no período.
  readonly subtotais: ReadonlyMap<number, { recebido: number; aReceber: number }>;
  // Os planos que tinham zero parcelas pagas no redesenho: só eles mostram a lixeira.
  readonly semPagas: ReadonlySet<number>;
}

// O modo de uma linha de parcela: normal, marcando como paga ou editando.
type ModoDaLinha =
  | { readonly modo: 'pagar'; readonly data: string }
  | { readonly modo: 'editar'; readonly valor: string; readonly data: string };

export function TelaFinanceiro() {
  const { acesso } = usePainel();
  // Quem não tem o Financeiro no menu não desenha nada aqui (o legado esconde a aba, `admin.html:3443`).
  if (!permissoesDoPapel(acesso.papel).includes('financeiro:ver')) return null;
  return <Financeiro />;
}

function Financeiro() {
  const { dados: fonte, acesso, rota, navegar } = usePainel();
  // "Apenas Consulta" vê tudo, com os campos travados e sem botão nenhum (`admin.html:3393` a `3395`).
  const soLeitura = acesso.papel === 'consulta';
  // A lixeira: todo mundo que não é consulta, pelo achado 3; só o administrador, sem ele.
  const lixeiraVisivel = !soLeitura && (acesso.papel === 'administrador' || ACHADO_3_FINANCEIRO_VE_LIXEIRA);
  const filtroRota = filtroDaRota(rota);
  const registrarId = filtroRota.get('registrar');
  const { confirmar, alertar, dialogo } = useConfirmacao();

  // Os dados vivos, e a referência que os fluxos assíncronos leem.
  const [mundo, setMundoEstado] = useState<Mundo>(MUNDO_VAZIO);
  const mundoRef = useRef<Mundo>(MUNDO_VAZIO);
  const setMundo = useCallback((trocar: (m: Mundo) => Mundo) => {
    mundoRef.current = trocar(mundoRef.current);
    setMundoEstado(mundoRef.current);
  }, []);
  // Os filtros dos campos; começam com as datas que o Painel mandou pela rota.
  const [filtros, setFiltrosEstado] = useState<FiltrosFinanceiro>(() => ({
    ...FILTROS_VAZIOS,
    dataIni: filtroRota.get('de') ?? '',
    dataFim: filtroRota.get('ate') ?? '',
  }));
  const filtrosRef = useRef(filtros);
  const setFiltros = (novos: FiltrosFinanceiro) => {
    filtrosRef.current = novos;
    setFiltrosEstado(novos);
  };
  // O estado da lista de planos: o legado escreve "Carregando..." e o erro só nela.
  const [lista, setLista] = useState<{ estado: 'carregando' | 'erro' | 'pronto'; erro?: string }>({ estado: 'carregando' });
  const [carregou, setCarregou] = useState(false);
  const [desenho, setDesenho] = useState<Desenho | null>(null);
  const [modos, setModos] = useState<Readonly<Record<number, ModoDaLinha>>>({});
  // O modal de valor diferente, o do relatório e a escolha de agrupamento (que o legado guarda entre aberturas).
  const [divergencia, setDivergencia] = useState<{ pagId: number; parcelaId: number; info: string; obs: string } | null>(null);
  const [pdfAberto, setPdfAberto] = useState(false);
  const [agrupamento, setAgrupamento] = useState<Agrupamento>('mes');

  /** O redesenho (`renderizarFinanceiro`, `admin.html:8241`): calcula tudo com os filtros dos campos. */
  const renderizar = useCallback((f: FiltrosFinanceiro) => {
    const m = mundoRef.current;
    const planos = filtrarPlanos(m.planos, f);
    const giftCards = filtrarGiftCards(m.giftCards, f);
    const cupons = filtrarCupons(m.cupons, f, m.leads);
    const { dataIni, dataFim } = limitesDeData(f);
    const subtotais = new Map(planos.map((p) => [p.id, somarParcelas(p.parcelas, dataIni, dataFim)] as const));
    const semPagas = new Set(planos.filter((p) => p.parcelas.every((x) => !x.pago)).map((p) => p.id));
    setDesenho({ resumo: totaisDoResumo(planos, giftCards, cupons, f), giftCards, cupons, planos: planos.map((p) => p.id), subtotais, semPagas });
    // O redesenho refaz as linhas: toda edição aberta volta ao normal.
    setModos({});
    setLista({ estado: 'pronto' });
  }, []);

  /** A carga (`carregarFinanceiro`, `admin.html:8134`), na mesma ordem e com o mesmo tratamento de erro. */
  const carregar = useCallback(async () => {
    setLista({ estado: 'carregando' });
    // Gift cards: falha só vai ao console, e a lista anterior fica (`admin.html:7150`).
    const gcs = await listarGiftCards(fonte);
    if (gcs.ok) setMundo((m) => ({ ...m, giftCards: gcs.dados ?? [] }));
    else console.error('Erro ao carregar gift cards:', gcs.erro.mensagem);
    // Cupons aplicados: falha vira lista vazia, como o `cuponsAplicados || []`.
    const cupons = await listarCuponsAplicados(fonte);
    setMundo((m) => ({ ...m, cupons: cupons.ok ? (cupons.dados ?? []) : [] }));
    // Os leads, que o legado já tinha do login com esta mesma consulta; falha vira lista vazia.
    const leads = await listarLeadsDoFunil(fonte);
    const todos = leads.ok ? (leads.dados ?? []) : [];
    setMundo((m) => ({ ...m, leads: todos }));
    // Só leads vinculados a uma cliente entram no Financeiro.
    const vinculados = todos.filter((l) => l.user_id_vinculado);
    if (vinculados.length === 0) {
      setMundo((m) => ({ ...m, planos: [] }));
      renderizar(filtrosRef.current);
      return setCarregou(true);
    }
    const pagamentos = await listarPagamentosDosLeads(fonte, vinculados.map((l) => l.id));
    if (!pagamentos.ok) {
      // O erro aparece só na lista; resumo e listas ficam como estavam, como no legado.
      setLista({ estado: 'erro', erro: 'Erro ao carregar: ' + pagamentos.erro.mensagem });
      return setCarregou(true);
    }
    if (!pagamentos.dados || pagamentos.dados.length === 0) {
      setMundo((m) => ({ ...m, planos: [] }));
      renderizar(filtrosRef.current);
      return setCarregou(true);
    }
    // Parcelas: o legado ignora o erro desta leitura, e o plano fica sem parcelas.
    const parcelas = await listarParcelasDosPagamentos(fonte, pagamentos.dados.map((p) => p.id));
    const porPlano = new Map<number, LinhaParcelas[]>();
    (parcelas.ok ? (parcelas.dados ?? []) : []).forEach((p) => {
      const doPlano = porPlano.get(p.pagamento_id) ?? [];
      doPlano.push(p);
      porPlano.set(p.pagamento_id, doPlano);
    });
    setMundo((m) => ({
      ...m,
      planos: (pagamentos.dados ?? []).map((pag) => ({
        ...pag,
        lead: vinculados.find((l) => String(l.id) === String(pag.lead_id)),
        parcelas: porPlano.get(pag.id) ?? [],
      })),
    }));
    renderizar(filtrosRef.current);
    setCarregou(true);
  }, [fonte, renderizar, setMundo]);

  // Abrir a área carrega do zero, como toda aba do legado (`admin.html:6446`).
  useEffect(() => {
    void carregar();
  }, [carregar]);

  /* -------------------------------------------------------------------------------------------
   * AÇÕES DE PARCELA (`acionarParcela`, `admin.html:8465`)
   * ----------------------------------------------------------------------------------------- */

  // Troca uma parcela nos dados vivos, sem redesenhar.
  function trocarParcela(pagId: number, parcela: LinhaParcelas) {
    setMundo((m) => ({
      ...m,
      planos: m.planos.map((p) =>
        p.id === pagId ? { ...p, parcelas: p.parcelas.map((x) => (x.id === parcela.id ? parcela : x)) } : p,
      ),
    }));
  }
  // O plano vivo mais novo.
  const planoVivo = (pagId: number) => mundoRef.current.planos.find((p) => p.id === pagId);
  // Volta a linha ao modo normal.
  const linhaNormal = (parcelaId: number) =>
    setModos((atual) => {
      const { [parcelaId]: _saiu, ...resto } = atual;
      return resto;
    });

  /** Quita o lead (`quitarLead`, `admin.html:8531`). `novaObs` só vem do "Confirmar mesmo assim". */
  async function quitarLead(pagId: number, novaObs?: string) {
    const pag = planoVivo(pagId);
    if (!pag) return;
    if (novaObs !== undefined) {
      // O total pago vira o novo valor combinado; o legado não confere o erro desta gravação.
      const totalPago = pag.parcelas.reduce((s, p) => s + p.valor, 0);
      await gravarQuitacaoComDiferenca(fonte, pag.id, novaObs || null, totalPago);
      setMundo((m) => ({
        ...m,
        planos: m.planos.map((p) => (p.id === pag.id ? { ...p, observacao: novaObs || null, valor_total: totalPago } : p)),
      }));
    }
    const r = await mudarStatusDoLead(fonte, pag.lead_id, 'pagamento_recebido');
    if (!r.ok) return void (await alertar('Erro ao atualizar status do lead: ' + r.erro.mensagem));
    setMundo((m) => ({ ...m, leads: m.leads.map((l) => (String(l.id) === String(pag.lead_id) ? { ...l, status: 'pagamento_recebido' } : l)) }));
    // A quitação redesenha a aba inteira, com os filtros que estiverem nos campos.
    renderizar(filtrosRef.current);
    await alertar(`Todas as parcelas de ${pag.lead?.nome || 'cliente'} foram quitadas! Lead movido para "Pagamento recebido".`);
  }

  /** Última parcela paga: quita, ou pergunta pela diferença (`verificarQuitacaoTotal`, `admin.html:8551`). */
  async function verificarQuitacao(pagId: number, parcelaId: number) {
    const pag = planoVivo(pagId);
    if (!pag || !pag.parcelas.every((p) => p.pago)) return;
    const totalPago = pag.parcelas.reduce((s, p) => s + p.valor, 0);
    const diferenca = totalPago - pag.valor_total;
    // Tolerância de 1 centavo.
    if (Math.abs(diferenca) > 0.01) {
      const sinal = diferenca > 0 ? '+' : '';
      setDivergencia({
        pagId,
        parcelaId,
        info: `Total combinado: ${formatarMoeda(pag.valor_total)} · Total pago: ${formatarMoeda(totalPago)} (${sinal}${formatarMoeda(Math.abs(diferenca))})`,
        obs: pag.observacao || '',
      });
      return;
    }
    await quitarLead(pagId);
  }

  /** Parcela que deixou de estar paga reabre o lead quitado (`reabrirPagamentoSeQuitado`, `admin.html:8519`). */
  async function reabrirSeQuitado(pagId: number) {
    const pag = planoVivo(pagId);
    if (!pag) return;
    // O status é lido no banco, e não no que a tela guarda, como o legado explica.
    const atual = await lerStatusDoLead(fonte, pag.lead_id);
    if (!atual.ok || !atual.dados || atual.dados.status !== 'pagamento_recebido') return;
    const r = await mudarStatusDoLead(fonte, pag.lead_id, 'pagamento_a_receber');
    if (!r.ok) return void (await alertar('Erro ao mover o lead de volta pra "Pagamento a receber": ' + r.erro.mensagem));
    setMundo((m) => ({
      ...m,
      leads: m.leads.map((l) => (String(l.id) === String(pag.lead_id) ? { ...l, status: 'pagamento_a_receber' } : l)),
      planos: m.planos.map((p) => (p.id === pagId && p.lead ? { ...p, lead: { ...p.lead, status: 'pagamento_a_receber' } } : p)),
    }));
    const nome = nomeDoLead(mundoRef.current.leads, pag.lead_id) ?? pag.lead?.nome;
    await alertar(`"${nome || 'Esse lead'}" voltou para "Pagamento a receber", já que nem todas as parcelas estão pagas.`, 'Lead movido de volta');
  }

  // "Confirmar" do marcar como pago (`admin.html:8471`).
  async function confirmarPago(pagId: number, parcela: LinhaParcelas, data: string) {
    if (!data) return void (await alertar('Informe a data de recebimento.'));
    const r = await marcarParcelaComoPaga(fonte, parcela.id, data);
    if (!r.ok) return void (await alertar('Erro ao marcar: ' + r.erro.mensagem));
    trocarParcela(pagId, { ...parcela, pago: true, data_pagamento: data });
    linhaNormal(parcela.id);
    await verificarQuitacao(pagId, parcela.id);
  }

  // "Desmarcar" (`admin.html:8483`).
  async function desmarcar(pagId: number, parcela: LinhaParcelas) {
    if (!(await confirmar('Desmarcar esta parcela como paga?'))) return;
    const r = await desmarcarParcela(fonte, parcela.id);
    if (!r.ok) return void (await alertar('Erro: ' + r.erro.mensagem));
    trocarParcela(pagId, { ...parcela, pago: false, data_pagamento: null });
    linhaNormal(parcela.id);
    await reabrirSeQuitado(pagId);
  }

  // "Salvar" da edição (`admin.html:8494`): a parcela volta a não paga.
  async function salvarEdicao(pagId: number, parcela: LinhaParcelas, valorTexto: string, data: string) {
    const novoValor = parseMoeda(valorTexto);
    if (!novoValor || !data) return void (await alertar('Preencha valor e data.'));
    const r = await editarParcela(fonte, parcela.id, novoValor, data);
    if (!r.ok) return void (await alertar('Erro ao salvar: ' + r.erro.mensagem));
    trocarParcela(pagId, { ...parcela, valor: novoValor, data_vencimento: data, pago: false, data_pagamento: null });
    linhaNormal(parcela.id);
    await reabrirSeQuitado(pagId);
  }

  // A lixeira do plano (`excluirPlanoPagamento`, `admin.html:8372`).
  async function excluirPlano(pag: PlanoFinanceiro) {
    const nome = pag.lead?.nome || 'esse lead';
    const ok = await confirmar(
      `Excluir o plano de pagamento de "${nome}"? O lead volta para a etapa em que estava antes de ir para "Pagamento a receber". Não tem como desfazer.`,
    );
    if (!ok) return;
    // A etapa anterior no histórico; sem histórico, "com_juridico", como o legado.
    const historico = await lerEtapaAnteriorAoPagamento(fonte, pag.lead_id);
    const anterior = (historico.ok ? historico.dados?.status_anterior : null) || 'com_juridico';
    await excluirPagamentoDoLead(fonte, pag.lead_id);
    const r = await mudarStatusDoLead(fonte, pag.lead_id, anterior);
    if (!r.ok) await alertar('Erro ao mover o lead de volta: ' + r.erro.mensagem);
    await carregar();
  }

  /* -------------------------------------------------------------------------------------------
   * MODAL DE VALOR DIFERENTE (`admin.html:8568` a `8581`)
   * ----------------------------------------------------------------------------------------- */

  // "Confirmar mesmo assim": grava a observação, o total pago e quita.
  async function confirmarDivergencia() {
    if (!divergencia) return;
    const obs = divergencia.obs.trim();
    setDivergencia(null);
    await quitarLead(divergencia.pagId, obs);
  }

  // "Cancelar pagamento": desfaz a última parcela paga.
  async function cancelarDivergencia() {
    if (!divergencia) return;
    const { pagId, parcelaId } = divergencia;
    setDivergencia(null);
    const r = await desmarcarParcela(fonte, parcelaId);
    if (!r.ok) return void (await alertar('Erro ao desfazer: ' + r.erro.mensagem));
    const parcela = planoVivo(pagId)?.parcelas.find((p) => p.id === parcelaId);
    if (parcela) trocarParcela(pagId, { ...parcela, pago: false, data_pagamento: null });
    linhaNormal(parcelaId);
  }

  /* -------------------------------------------------------------------------------------------
   * RELATÓRIO PDF (`admin.html:8597` a `8627`)
   * ----------------------------------------------------------------------------------------- */

  // "Gerar relatório PDF": sem nada carregado, o aviso; senão, a escolha do agrupamento.
  function abrirPdf() {
    const m = mundoRef.current;
    if (m.planos.length === 0 && m.giftCards.length === 0 && m.cupons.length === 0) {
      void alertar('Carregue os dados do Financeiro primeiro.');
      return;
    }
    setPdfAberto(true);
  }

  // "Gerar": usa os filtros dos campos AGORA, e abre a janela dentro do clique.
  function gerarPdf() {
    setPdfAberto(false);
    const m = mundoRef.current;
    const f = filtrosRef.current;
    const planos = filtrarPlanos(m.planos, f);
    const giftCards = filtrarGiftCards(m.giftCards, f);
    const cupons = filtrarCupons(m.cupons, f, m.leads);
    if (planos.length === 0 && giftCards.length === 0 && cupons.length === 0) {
      void alertar('Nenhum dado para gerar o relatório.');
      return;
    }
    abrirRelatorio(
      montarHtmlRelatorio({
        planos,
        agrupamento,
        filtros: f,
        totais: totaisDoResumo(planos, giftCards, cupons, f),
        giftCards,
        cupons,
        leads: m.leads,
        cores: coresDoRelatorio(),
      }),
    );
  }

  /* -------------------------------------------------------------------------------------------
   * DESENHO
   * ----------------------------------------------------------------------------------------- */

  // O lead que a Vendas mandou registrar, quando já carregado.
  const leadARegistrar = registrarId ? mundo.leads.find((l) => String(l.id) === registrarId) : undefined;

  // As colunas da tabela de parcelas de um plano (`admin.html:8360` e `8393` a `8455`).
  function colunasDoPlano(pag: PlanoFinanceiro): Coluna<LinhaParcelas>[] {
    const total = pag.parcelas.length;
    return [
      { chave: 'parc', titulo: 'Parc.', celula: (p) => `${p.numero}/${total}` },
      {
        chave: 'valor',
        titulo: 'Valor',
        celula: (p) => {
          const modo = modos[p.id];
          if (modo?.modo !== 'editar') return formatarMoeda(p.valor);
          return (
            <Campo
              rotulo="Valor"
              value={modo.valor}
              onChange={(ev) => setModos((a) => ({ ...a, [p.id]: { ...modo, valor: mascararMoedaLegado(ev.target.value) } }))}
            />
          );
        },
      },
      {
        chave: 'venc',
        titulo: 'Vencimento',
        celula: (p) => {
          const modo = modos[p.id];
          if (modo?.modo !== 'editar') return dataBR(p.data_vencimento);
          return (
            <Campo
              rotulo="Vencimento"
              type="date"
              value={modo.data}
              onChange={(ev) => setModos((a) => ({ ...a, [p.id]: { ...modo, data: ev.target.value } }))}
            />
          );
        },
      },
      {
        chave: 'status',
        titulo: 'Status',
        celula: (p) => {
          const modo = modos[p.id];
          // Marcando como paga: a data de recebimento ocupa o lugar do estado (no legado, duas colunas).
          if (modo?.modo === 'pagar') {
            return (
              <Campo
                rotulo="Data de recebimento"
                type="date"
                value={modo.data}
                onChange={(ev) => setModos((a) => ({ ...a, [p.id]: { ...modo, data: ev.target.value } }))}
              />
            );
          }
          if (modo?.modo === 'editar') return <span className="text-cx-xs text-texto-suave">Salvar redefine a parcela como não paga.</span>;
          const s = statusParcela(p);
          return <Selo tom={TOM_DO_SELO[s]}>{ROTULO_STATUS_PARCELA[s]}</Selo>;
        },
      },
      {
        chave: 'pagoem',
        titulo: 'Pago em',
        celula: (p) => (modos[p.id] ? '' : p.data_pagamento ? dataBR(p.data_pagamento) : '-'),
      },
      {
        chave: 'acoes',
        titulo: '',
        celula: (p) => {
          // Consulta não vê botão nenhum.
          if (soLeitura) return null;
          const modo = modos[p.id];
          return (
            <div className="flex flex-wrap justify-end gap-cx-2">
              {modo?.modo === 'pagar' ? (
                <>
                  <Botao tamanho="sm" onClick={() => void confirmarPago(pag.id, p, modo.data)}>
                    Confirmar
                  </Botao>
                  <Botao tamanho="sm" variante="secundario" onClick={() => linhaNormal(p.id)}>
                    Cancelar
                  </Botao>
                </>
              ) : modo?.modo === 'editar' ? (
                <>
                  <Botao tamanho="sm" onClick={() => void salvarEdicao(pag.id, p, modo.valor, modo.data)}>
                    Salvar
                  </Botao>
                  <Botao tamanho="sm" variante="secundario" onClick={() => linhaNormal(p.id)}>
                    Cancelar
                  </Botao>
                </>
              ) : (
                <>
                  {p.pago ? (
                    <Botao tamanho="sm" variante="secundario" onClick={() => void desmarcar(pag.id, p)}>
                      Desmarcar
                    </Botao>
                  ) : (
                    // A data de recebimento começa em hoje, calculado em UTC como no legado.
                    <Botao tamanho="sm" onClick={() => setModos((a) => ({ ...a, [p.id]: { modo: 'pagar', data: hojeUTC() } }))}>
                      Marcar como pago
                    </Botao>
                  )}
                  <Botao
                    tamanho="sm"
                    variante="secundario"
                    onClick={() =>
                      setModos((a) => ({ ...a, [p.id]: { modo: 'editar', valor: formatarMoeda(p.valor), data: p.data_vencimento } }))
                    }
                  >
                    Editar
                  </Botao>
                </>
              )}
            </div>
          );
        },
      },
    ];
  }

  return (
    <div className="flex flex-col gap-cx-5" data-testid="tela-financeiro">
      {/* Os filtros em linha; só valem ao clicar "Filtrar" (`admin.html:1860` a `1900`). */}
      <LinhaCompacta>
        <Campo
          rotulo="Data inicial"
          type="date"
          disabled={soLeitura}
          value={filtros.dataIni}
          onChange={(ev) => setFiltros({ ...filtros, dataIni: ev.target.value })}
        />
        <Campo
          rotulo="Data final"
          type="date"
          disabled={soLeitura}
          value={filtros.dataFim}
          onChange={(ev) => setFiltros({ ...filtros, dataFim: ev.target.value })}
        />
        <Selecao
          rotulo="Status"
          vazio="Todos"
          opcoes={OPCOES_STATUS}
          disabled={soLeitura}
          value={filtros.status}
          onChange={(ev) => setFiltros({ ...filtros, status: ev.target.value })}
        />
        <Selecao
          rotulo="Forma"
          vazio="Todas"
          opcoes={OPCOES_FORMA_FILTRO}
          disabled={soLeitura}
          value={filtros.forma}
          onChange={(ev) => setFiltros({ ...filtros, forma: ev.target.value })}
        />
        <Campo
          rotulo="Cliente"
          placeholder="Nome…"
          disabled={soLeitura}
          value={filtros.cliente}
          onChange={(ev) => setFiltros({ ...filtros, cliente: ev.target.value })}
        />
        {!soLeitura && (
          <>
            <Botao tamanho="sm" onClick={() => renderizar(filtrosRef.current)}>
              Filtrar
            </Botao>
            <Botao
              tamanho="sm"
              variante="secundario"
              onClick={() => {
                setFiltros(FILTROS_VAZIOS);
                renderizar(FILTROS_VAZIOS);
              }}
            >
              Limpar
            </Botao>
            {/* No fim da linha, separado dos dois de filtro, como `filtros-acoes-fim`. */}
            <span className="ml-auto">
              <Botao tamanho="sm" variante="secundario" onClick={abrirPdf}>
                Gerar relatório PDF
              </Botao>
            </span>
          </>
        )}
      </LinhaCompacta>

      {/* O resumo com os quatro números (`admin.html:8264`); vazio até o primeiro redesenho. */}
      {desenho && (
        <Cartao>
          <div className="grid grid-cols-2 gap-cx-4 sm:grid-cols-4" data-testid="fin-resumo">
            <Numero rotulo="Recebido" valor={desenho.resumo.recebido} tom="text-sucesso" />
            <Numero rotulo="A receber" valor={desenho.resumo.aReceber} tom="text-erro" />
            <Numero rotulo="Total geral" valor={desenho.resumo.recebido + desenho.resumo.aReceber} tom="text-texto" />
            <Numero rotulo="Descontos em cupons" valor={desenho.resumo.descontos} tom="text-erro" />
          </div>
        </Cartao>
      )}

      <section className="flex flex-col gap-cx-2">
        <h2 className="text-cx-base font-semibold text-texto">Gift Cards emitidos</h2>
        <div data-testid="fin-giftcards">
          {desenho &&
            (desenho.giftCards.length === 0 ? (
              <p className="text-cx-sm text-texto-suave">Nenhum gift card emitido nesse período.</p>
            ) : (
              <Cartao semRespiro>
                {desenho.giftCards.map((gc) => (
                  <div key={gc.id} className="flex flex-wrap items-center justify-between gap-cx-2 border-b border-borda px-cx-4 py-cx-3 last:border-0">
                    <div>
                      <div className="text-cx-sm text-texto">
                        <strong>{gc.codigo}</strong> · {formatarMoeda(Number(gc.valor))}
                      </div>
                      <div className="text-cx-xs text-texto-suave">
                        De {gc.nome_comprador ?? ''} pra {gc.nome_destinatario ?? ''} · emitido em {dataHoraBR(gc.emitido_em)}
                      </div>
                    </div>
                    <Selo tom={TOM_DO_SELO[seloDoGiftCard(gc)]}>{textoDoGiftCard(gc, mundo.leads)}</Selo>
                  </div>
                ))}
              </Cartao>
            ))}
        </div>
      </section>

      <section className="flex flex-col gap-cx-2">
        <h2 className="text-cx-base font-semibold text-texto">Cupons utilizados</h2>
        <div data-testid="fin-cupons">
          {desenho &&
            (desenho.cupons.length === 0 ? (
              <p className="text-cx-sm text-texto-suave">Nenhum cupom utilizado nesse período.</p>
            ) : (
              <Cartao semRespiro>
                {desenho.cupons.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center justify-between gap-cx-2 border-b border-borda px-cx-4 py-cx-3 last:border-0">
                    <div>
                      <div className="text-cx-sm text-texto">
                        <strong>{r.codigo}</strong> · {nomeDoLead(mundo.leads, r.lead_id) || '(lead removido)'}
                      </div>
                      <div className="text-cx-xs text-texto-suave">aplicado em {dataHoraBR(r.aplicado_em ?? '')}</div>
                    </div>
                    <span className="font-semibold text-erro">-{formatarMoeda(Number(r.valor_aplicado))}</span>
                  </div>
                ))}
              </Cartao>
            ))}
        </div>
      </section>

      <section className="flex flex-col gap-cx-3">
        <h2 className="text-cx-base font-semibold text-texto">Planos de pagamento</h2>
        <div className="flex flex-col gap-cx-4" data-testid="fin-lista">
          {lista.estado === 'carregando' ? (
            <p className="text-cx-sm text-texto-suave">Carregando...</p>
          ) : lista.estado === 'erro' ? (
            <p className="text-cx-sm text-texto-suave">{lista.erro}</p>
          ) : !desenho || desenho.planos.length === 0 ? (
            <p className="text-cx-sm text-texto-suave">Nenhum plano de pagamento encontrado.</p>
          ) : (
            desenho.planos.map((id) => {
              const pag = mundo.planos.find((p) => p.id === id);
              if (!pag) return null;
              const pagas = pag.parcelas.filter((p) => p.pago).length;
              const sub = desenho.subtotais.get(id) ?? { recebido: 0, aReceber: 0 };
              return (
                <Cartao key={id}>
                  <div className="flex flex-col gap-cx-3" data-testid={`fin-plano-${id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-cx-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-cx-2 font-semibold text-texto">
                          <span>{pag.lead?.nome || '(sem nome)'}</span>
                          {pag.lead?.contato_whatsapp && (
                            <span className="inline-flex items-center text-cx-sm font-normal text-texto-suave">
                              {formatarTelefone(pag.lead.contato_whatsapp)}
                              <LinkWhatsapp telefone={pag.lead.contato_whatsapp} />
                            </span>
                          )}
                        </div>
                        {/* A linha que muda sozinha ao marcar e desmarcar (`atualizarMetaCard`). */}
                        <div className="text-cx-sm text-texto-suave" data-testid="fin-meta">
                          {FORMAS_PAGAMENTO[pag.forma_pagamento ?? ''] || pag.forma_pagamento} · {pagas}/{pag.parcelas.length} parcelas pagas
                        </div>
                        {pag.observacao && <div className="mt-cx-1 text-cx-xs italic text-texto-suave">{pag.observacao}</div>}
                      </div>
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-cx-2">
                          {/* A lixeira só com zero pagas NO REDESENHO, e pelo papel (achado 3). */}
                          {desenho.semPagas.has(id) && lixeiraVisivel && (
                            <Botao
                              variante="fantasma"
                              tamanho="icone"
                              title="Excluir plano de pagamento"
                              aria-label="Excluir plano de pagamento"
                              onClick={() => void excluirPlano(pag)}
                            >
                              <IconeLixeira aria-hidden="true" className="h-cx-4 w-cx-4" />
                            </Botao>
                          )}
                          <span className="text-cx-lg font-semibold tabular-nums text-texto">{formatarMoeda(pag.valor_total)}</span>
                        </div>
                        <div className="flex flex-col text-cx-xs tabular-nums">
                          <span className="text-sucesso">Recebido: {formatarMoeda(sub.recebido)}</span>
                          <span className="text-erro">A receber: {formatarMoeda(sub.aReceber)}</span>
                        </div>
                      </div>
                    </div>
                    <Tabela
                      rotulo={`Parcelas de ${pag.lead?.nome || '(sem nome)'}`}
                      colunas={colunasDoPlano(pag)}
                      linhas={pag.parcelas}
                      chaveDaLinha={(p) => String(p.id)}
                      vazio=""
                    />
                  </div>
                </Cartao>
              );
            })
          )}
        </div>
      </section>

      {/* O modal de valor diferente (`admin.html:2962`). */}
      <ModalLegado
        aberto={divergencia !== null}
        // ACHADO 6, reproduzido por decisão do fundador: X, ESC e clique fora SÓ fecham; a parcela
        // continua paga e o lead não é quitado (`admin.html:7720` a `7751`). Corrigir é trocar esta
        // linha por `cancelarDivergencia`.
        aoFechar={() => setDivergencia(null)}
        titulo="Atenção: valor diferente"
        testid="modal-divergencia"
        rodape={
          <>
            <Botao variante="secundario" onClick={() => void cancelarDivergencia()}>
              Cancelar pagamento
            </Botao>
            <Botao onClick={() => void confirmarDivergencia()}>Confirmar mesmo assim</Botao>
          </>
        }
      >
        <div className="flex flex-col gap-cx-3">
          <p className="text-texto-suave" data-testid="divergencia-info">
            {divergencia?.info}
          </p>
          <CampoTexto
            rotulo="Observação sobre a diferença"
            placeholder="Ex: cliente pagou valor diferente por acordo..."
            linhas={3}
            value={divergencia?.obs ?? ''}
            onChange={(ev) => setDivergencia((d) => (d ? { ...d, obs: ev.target.value } : d))}
          />
        </div>
      </ModalLegado>

      {/* O modal do Relatório PDF (`admin.html:2994`). */}
      <ModalLegado
        aberto={pdfAberto}
        aoFechar={() => setPdfAberto(false)}
        titulo="Relatório PDF"
        testid="modal-pdf"
        rodape={
          <>
            <Botao variante="secundario" onClick={() => setPdfAberto(false)}>
              Cancelar
            </Botao>
            <Botao onClick={gerarPdf}>Gerar</Botao>
          </>
        }
      >
        <div className="flex flex-col gap-cx-3">
          <p className="text-texto-suave">Como deseja agrupar as informações?</p>
          <GrupoRadio
            nome="agrupamento-pdf"
            rotuloDoGrupo="Como deseja agrupar as informações?"
            opcoes={OPCOES_AGRUPAMENTO}
            valor={agrupamento}
            aoMudar={(v) => setAgrupamento(v as Agrupamento)}
            empilhado
          />
        </div>
      </ModalLegado>

      {/* O modal de pagamento que a Vendas abre pela rota, quando o lead já foi carregado. */}
      {carregou && leadARegistrar && (
        <RegistrarPagamento
          lead={leadARegistrar}
          giftCards={mundo.giftCards}
          aoCancelar={() => navegar('/vendas')}
          aoConcluir={() => {
            navegar('/financeiro');
            void carregar();
          }}
        />
      )}

      {dialogo}
    </div>
  );
}

/** Um número do resumo: o rótulo pequeno em maiúsculas e o valor na cor do legado. */
function Numero({ rotulo, valor, tom }: { readonly rotulo: string; readonly valor: number; readonly tom: string }) {
  return (
    <div className="flex flex-col gap-cx-1">
      <span className="text-cx-xs font-semibold uppercase tracking-wide text-texto-fraco">{rotulo}</span>
      <span className={`text-cx-lg font-semibold tabular-nums ${tom}`}>{formatarMoeda(valor)}</span>
    </div>
  );
}
